/**
 * Wave H — dimensioning & annotation commands. The existing CAD dimension
 * renderer family is reused; new subtypes carry data for LinearDimension/
 * Radial renderers. (command-parity-plan.md)
 */

import {
    runInteraction, PT, PK, PN, PLoopK, PTxt, requireSelection, entityToPolyline, makeEntity, rubberBand,
} from './lib';
import { undoableAdd, undoableModify } from '../entityOps';
import { useAppStore } from '../../../state/store';
import { useDimStyleStore } from '../../../tools/cad/DimensioningTools';
import { useLeaderStyleStore } from '../../../state/commandAuxStores';
import { polylineLength, polygonArea, centroid, lineIntersection2 } from '../geometryMath';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';

function dimEntity(type: string, data: Record<string, unknown>, layerId?: string) {
    return makeEntity('dimension', `Dim-${type}-${Date.now()}`, {
        type,
        // Stamp the CURRENT dim style so the renderers draw this dimension
        // with its own style (styleId), not the active-style snapshot.
        styleId: useDimStyleStore.getState().activeStyleId,
        ...data,
    }, layerId);
}

function textEntity(content: string, pos: Pt3, opts: { multiline?: boolean; styleId?: string } = {}) {
    return makeEntity(opts.multiline ? 'mtext' : 'text', `Text-${Date.now()}`, {
        content, position: pos, styleId: opts.styleId ?? 'standard',
        rotation: 0, multiline: !!opts.multiline,
    });
}

/** DDEDIT helpers (docs/plans/2d plan 5.4): double-click pre-selects a
 *  text entity; the command then jumps straight to the content prompt. */
function selectedEditableText() {
    const { entities, selectedIds } = useAppStore.getState();
    const id = selectedIds.find((i) => ['text', 'mtext', 'dimension', 'attdef'].includes(entities[i]?.type ?? ''));
    return id ? entities[id] : null;
}
function previewOf(e: { data: Record<string, unknown>; type: string }): string {
    const raw = String(e.data.content ?? e.data.override ?? e.data.tag ?? e.type);
    return raw.length > 18 ? `${raw.slice(0, 18)}…` : raw;
}
function applyDdEdit(id: string, content: string, type?: string): string {
    const e = useAppStore.getState().entities[id];
    const etype = type ?? e?.type ?? 'text';
    if (!e) return 'DDEDIT: object not found.';
    undoableModify([id], (d) => {
        const next = { ...d };
        if (etype === 'dimension') next.override = content;
        else if (etype === 'attdef') next.defaultValue = content;
        else next.content = content;
        return next;
    });
    return `DDEDIT: text updated (${etype}).`;
}

function valueNum(v: number | string | undefined): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
}

export const dimRunners: RunnerMap = {    // Active tool for DIMLINEAR-style placement, exposed as actions so the
    // command line can also start them via preset/registry.
    'draw.mtext': () => {
        runInteraction({
            steps: [PTxt('MTEXT: text content'), PT('MTEXT: insertion point')],
            complete: (pts, values) => {
                undoableAdd([textEntity(String(values[0] ?? 'Text'), [pts[0].x, pts[0].y, pts[0].z], { multiline: true })]);
                return 'Multiline text placed.';
            },
        });
        return 'MTEXT: enter text';
    },
    'draw.text': () => {
        // Actually activate the CAD text hook (ToolScope mounts the 'text'
        // tool scope); previously this returned a message and did nothing.
        useAppStore.getState().setActiveTool('text');
        return 'TEXT tool activated via command.';
    },

    // DDEDIT: edit placed text/mtext/dimension content. With PICKFIRST and a
    // text already selected, the command goes straight to the new-content
    // prompt (double-click on the text pre-selects it — see EntityLayer).
    'draw.ddedit': () => {
        const selected = selectedEditableText();
        if (selected) {
            runInteraction({
                steps: [PTxt(`DDEDIT: new content for "${previewOf(selected)}"`)],
                complete: (_p, values) => applyDdEdit(selected.id, String(values[0] ?? '')),
            });
            return 'DDEDIT: type the new text content';
        }
        runInteraction({
            steps: [
                PK('DDEDIT: pick text or a dimension to edit', ['text', 'mtext', 'dimension', 'attdef']),
                PTxt('DDEDIT: new text content'),
            ],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'DDEDIT: invalid pick.';
                return applyDdEdit(e.id, String(values[1] ?? ''), e.type);
            },
        });
        return 'DDEDIT: pick the text to edit';
    },

    'dim.diameter': () => {
        runInteraction({
            steps: [PK('DIMDIAMETER: pick a circle/arc')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'DIMDIAMETER: invalid pick.';
                const c = (e.data.center as number[]) ?? [0, 0, 0];
                const r = (e.data.radius as number) ?? 1;
                undoableAdd([dimEntity('diameter', { center: c, radiusPoint: [c[0] + r, c[1], c[2] ?? 0], radius: r, textPosition: [c[0], c[1] + r * 0.4, (c[2] ?? 0)] })]);
                return `Diameter dimension Ø${(r * 2).toFixed(2)} added.`;
            },
        });
        return 'DIMDIAMETER: pick a circle';
    },

    'dim.angular': () => {
        runInteraction({
            steps: [PK('DIMANGULAR: pick first line'), PK('DIMANGULAR: pick second line'), PT('DIMANGULAR: text location')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const l1 = entityToPolyline(entities[picks[0]]);
                const l2 = entityToPolyline(entities[picks[1]]);
                if (!l1 || !l2) return 'DIMANGULAR: pick two lines.';
                // Only one point step exists: the text location is pts[0].
                const textP = [pts[0].x, pts[0].y, pts[0].z];
                const d1 = [l1[l1.length - 1][0] - l1[0][0], l1[l1.length - 1][1] - l1[0][1]];
                const d2 = [l2[l2.length - 1][0] - l2[0][0], l2[l2.length - 1][1] - l2[0][1]];
                const cos = (d1[0] * d2[0] + d1[1] * d2[1])
                    / (Math.hypot(d1[0], d1[1]) * Math.hypot(d2[0], d2[1]) || 1);
                const deg = Math.abs(Math.acos(Math.max(-1, Math.min(1, cos))) * 180 / Math.PI);
                // Vertex = where the two measured lines meet (infinite-line
                // solution); the arc renderer draws rays + measurement arc
                // from it, so the label shows the ANGLE not a chord.
                const vertex = lineIntersection2(
                    { a: l1[0], b: l1[l1.length - 1] },
                    { a: l2[0], b: l2[l2.length - 1] }
                ) ?? [(l1[0][0] + l2[0][0]) / 2, (l1[0][1] + l2[0][1]) / 2, l1[0][2] ?? 0];
                undoableAdd([dimEntity('angular', {
                    center: vertex,
                    point1: l1[l1.length - 1], point2: l2[l2.length - 1],
                    angle: deg,
                    textPosition: textP,
                    override: `${deg.toFixed(1)}°`,
                })]);
                return `Angular dimension ${deg.toFixed(1)}° added.`;
            },
        });
        return 'DIMANGULAR: pick first line';
    },

    'dim.arcLength': () => {
        runInteraction({
            steps: [PK('DIMARC: pick an arc'), PT('DIMARC: text location')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const poly = entityToPolyline(entities[picks[0]]);
                if (!poly) return 'DIMARC: unsupported object.';
                const len = polylineLength(poly);
                undoableAdd([dimEntity('arcLength', {
                    point1: poly[0], point2: poly[poly.length - 1],
                    // The own-subtype renderer follows the ARC path (not the
                    // chord): pass the tessellation + the computed length.
                    arcPts: poly.map((q) => [...q]),
                    arcLength: len,
                    textPosition: [pts[0].x, pts[0].y, pts[0].z],
                    override: len.toFixed(2),
                })]);
                return `Arc-length dimension (${len.toFixed(3)}) added.`;
            },
        });
        return 'DIMARC: pick an arc';
    },

    'dim.continue': () => {
        runInteraction({
            steps: [PK('DIMCONTINUE: pick an existing dimension'), PT('DIMCONTINUE: next extension point')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const src = entities[picks[0]];
                if (!src || src.type !== 'dimension') return 'DIMCONTINUE: pick a dimension first.';
                const prev = (src.data.point2 as Pt3) ?? [pts[0].x, pts[0].y, pts[0].z];
                undoableAdd([dimEntity('continue', { point1: prev, point2: [pts[0].x, pts[0].y, pts[0].z], textPosition: [pts[0].x, pts[0].y + 0.5, pts[0].z] })]);
                return 'Chained dimension continued.';
            },
        });
        return 'DIMCONTINUE: pick a dimension';
    },

    'dim.baseline': () => {
        runInteraction({
            steps: [PK('DIMBASELINE: pick the base dimension'), PT('DIMBASELINE: next point')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const src = entities[picks[0]];
                if (!src || src.type !== 'dimension') return 'DIMBASELINE: pick a dimension first.';
                const origin = (src.data.point1 as Pt3) ?? [pts[0].x, pts[0].y, pts[0].z];
                undoableAdd([dimEntity('baseline', { point1: origin, point2: [pts[0].x, pts[0].y, pts[0].z], textPosition: [pts[0].x, pts[0].y + 1, pts[0].z] })]);
                return 'Baseline dimension added.';
            },
        });
        return 'DIMBASELINE: pick a dimension';
    },

    'dim.jogged': () => {
        runInteraction({
            steps: [PK('DIMJOGGED: pick an arc'), PT('DIMJOGGED: text location')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const c = entities[picks[0]]?.data.center as number[] | undefined;
                if (!c) return 'DIMJOGGED: pick a circle/arc.';
                undoableAdd([dimEntity('jogged', { center: c, radius: entities[picks[0]].data.radius ?? 1, radiusPoint: [pts[0].x, pts[0].y, pts[0].z] })]);
                return 'Jogged radius dimension added.';
            },
        });
        return 'DIMJOGGED: pick an arc';
    },

    'dim.space': () => {
        runInteraction({
            steps: [PLoopK('DIMSPACE: pick parallel linear dimensions (2+)', 2), PN('DIMSPACE: spacing <1>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const dims = picks.map((id) => entities[id]).filter((e) => e?.type === 'dimension');
                if (dims.length < 2) return 'DIMSPACE: pick at least two dimensions.';
                const spacing = Number(values[1]) > 0 ? Number(values[1]) : 1;
                // Sort by distance of textPosition from the first dim's base
                // line and re-stack them with uniform spacing along the
                // average extension direction.
                const first = dims[0];
                const p1 = (first.data.point1 as number[]) ?? [0, 0, 0];
                const p2 = (first.data.point2 as number[]) ?? [1, 0, 0];
                let ux = p2[1] - p1[1], uy = -(p2[0] - p1[0]);
                const ul = Math.hypot(ux, uy) || 1;
                ux /= ul; uy /= ul;
                const proj = (e: typeof first) => {
                    const t = (e.data.textPosition as number[]) ?? [0, 0, 0];
                    return (t[0] - p1[0]) * ux + (t[1] - p1[1]) * uy;
                };
                const sorted = [...dims].sort((a, b) => proj(a) - proj(b));
                const base = proj(sorted[0]);
                const ids = sorted.map((d) => d.id);
                const snapshots = new Map(ids.map((id) => [id, JSON.stringify(entities[id].data)]));
                undoableModify(ids, (d, e) => {
                    const idx = ids.indexOf(e.id);
                    const tp = Array.isArray(d.textPosition) ? [...(d.textPosition as number[])] : [p1[0], p1[1], p1[2] ?? 0];
                    const cur = proj(e);
                    tp[0] += (base + idx * spacing - cur) * ux;
                    tp[1] += (base + idx * spacing - cur) * uy;
                    return { ...d, textPosition: tp };
                });
                void snapshots;
                return `DIMSPACE: ${ids.length} dimensions spaced at ${spacing}.`;
            },
        });
        return 'DIMSPACE: pick parallel dimensions';
    },

    'dim.break': () => {
        runInteraction({
            steps: [PK('DIMBREAK: pick a dimension')],
            complete: (_p, _v, picks) => {
                undoableModify(picks, (d) => ({ ...d, breakAtCrossing: true }));
                return 'DIMBREAK: crossing break flag set.';
            },
        });
        return 'DIMBREAK: pick a dimension';
    },

    'dim.style': (args) => {
        // DIMSTYLE with a name argument creates/switches directly; with no
        // arguments open the full Dimension Style Manager dialog (parity
        // with the 2D DIMSTYLE command).
        const name = args?.join(' ')?.trim();
        const store = useDimStyleStore.getState();
        if (!name) {
            useAppStore.getState().openManageDialog('dimStyles');
            return 'DIMSTYLE: Dimension Style Manager opened.';
        }
        const existing = Object.values(store.styles).find((s) => s.name.toLowerCase() === name.toLowerCase());
        if (existing) {
            store.setActiveStyle(existing.id);
            return `DIMSTYLE: style "${existing.name}" set current.`;
        }
        const base = store.getActiveStyle();
        const id = name.toLowerCase().replace(/\s+/g, '-');
        store.addStyle({ ...base, id, name });
        store.setActiveStyle(id);
        return `Dimension style "${name}" created and activated.`;
    },

    'dim.reassociate': () => {
        runInteraction({
            steps: [PK('DIMREASSOCIATE: pick a dimension'), PK('DIMREASSOCIATE: pick the geometry to bind to')],
            complete: (_p, _v, picks) => {
                const src = useAppStore.getState().entities[picks[1]];
                const ref = src ? entityToPolyline(src) : null;
                undoableModify([picks[0]], (d) => ({
                    ...d,
                    assoc: [picks[1]],
                    // Bind-time snapshot of the source geometry: the associator
                    // engine (core/commands/dimAssociation) replays source
                    // movements onto this dimension's definition points.
                    assocRef: ref ? { pts: ref.map((q) => [...q]), centroid: centroid(ref) } : undefined,
                }));
                return 'Dimension reassociated to the picked geometry (associative).';
            },
        });
        return 'DIMREASSOCIATE: pick dimension then geometry';
    },
    'dim.disassociate': () => {
        const list = requireSelection('DIMDISASSOCIATE');
        if (!list) return 'DIMDISASSOCIATE: select dimensions first.';
        undoableModify(list.map((e) => e.id), (d) => { delete d.assoc; return d; });
        return `DIMDISASSOCIATE: ${list.length} object(s) detached.`;
    },
    'dim.inspect': () => {
        runInteraction({
            steps: [PK('DIMINSPECT: pick a dimension'), PTxt('DIMINSPECT: inspection label')],
            complete: (_p, values, picks) => {
                undoableModify(picks, (d) => ({ ...d, inspection: String(values[1] ?? 'OK') }));
                return 'Inspection label attached to the dimension.';
            },
        });
        return 'DIMINSPECT: pick dimension';
    },
    'dim.override': () => {
        runInteraction({
            steps: [PK('DIMOVERRIDE: pick a dimension'), PTxt('DIMOVERRIDE: override text')],
            complete: (_p, values, picks) => {
                undoableModify(picks, (d) => ({ ...d, override: String(values[1] ?? '') }));
                return 'Dimension text overridden.';
            },
        });
        return 'DIMOVERRIDE: pick dimension';
    },
    'dim.centerMark': () => {
        runInteraction({
            steps: [PK('CENTERMARK: pick a circle/arc'), PN('CENTERMARK: mark size <0.3>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const c = entities[picks[0]]?.data.center as number[] | undefined;
                if (!c) return 'CENTERMARK: pick a circle/arc.';
                const s = Number(values[1]) || 0.3;
                undoableAdd([
                    makeEntity('line', `CenterMark-${Date.now()}`, { start: [c[0] - s, c[1], c[2] ?? 0], end: [c[0] + s, c[1], c[2] ?? 0], color: '#cc4444' }),
                    makeEntity('line', `CenterMark-${Date.now()}`, { start: [c[0], c[1] - s, c[2] ?? 0], end: [c[0], c[1] + s, c[2] ?? 0], color: '#cc4444' }),
                ]);
                return 'Center mark added.';
            },
        });
        return 'CENTERMARK: pick a circle';
    },
    'dim.tolerance': () => {
        runInteraction({
            steps: [
                PTxt('TOLERANCE: cells separated by | (e.g. |0.1|A|B — first cell may be a ⌒⌀⏥ symbol)'),
                PT('TOLERANCE: frame insertion point (left edge)'),
            ],
            complete: (pts, values) => {
                const raw = String(values[0] ?? '').trim();
                const cells = (raw.split('|').map((c) => c.trim()).filter(Boolean));
                const frame = cells.length ? cells : ['±0.0'];
                const p = pts[0];
                undoableAdd([makeEntity('dimension', `Dim-tolerance-${Date.now()}`, {
                    type: 'tolerance',
                    cells: frame,
                    position: [p.x, p.y, p.z],
                    height: 0.3,
                    color: '#ffffff',
                })]);
                return `Feature control frame placed: [${frame.join(' | ')}].`;
            },
        });
        return 'TOLERANCE: enter frame cells';
    },

    // ---------------- annotation ----------------
    'ann.leader': () => {
        const ls = useLeaderStyleStore.getState();
        const style = ls.styles[ls.activeId] ?? ls.styles['standard'];
        runInteraction({
            steps: [PT('MLEADER: arrow tip point'), PT('MLEADER: landing point'), PTxt('MLEADER: note text')],
            complete: (pts, values) => {
                undoableAdd([makeEntity('leader', `Leader-${Date.now()}`, {
                    tip: [pts[0].x, pts[0].y, pts[0].z], landing: [pts[1].x, pts[1].y, pts[1].z],
                    text: String(values[2] ?? 'Note'),
                    styleId: style.id,
                    color: style.color,
                    textScale: style.textScale,
                })]);
                return `Multileader placed (style "${style.name}").`;
            },
        });
        return 'MLEADER: tip point';
    },
    // QLEADER: same leader entity, simpler 3-pick flow (tip, knee, text).
    'ann.qleader': () => {
        runInteraction({
            steps: [PT('QLEADER: first leader point'), PT('QLEADER: next point'), PTxt('QLEADER: annotation text')],
            preview: rubberBand('#ffff00'),
            complete: (pts, values) => {
                const tip = pts[0], knee = pts[1];
                undoableAdd([makeEntity('leader', `Leader-${Date.now()}`, {
                    tip: [tip.x, tip.y, tip.z],
                    landing: [knee.x, knee.y, knee.z],
                    text: String(values[2] ?? 'Note'),
                    qleader: true,
                })]);
                return 'Quick leader placed.';
            },
        });
        return 'QLEADER: specify first leader point';
    },
    'ann.leaderStyle': () => {
        runInteraction({
            steps: [
                PTxt('MLEADERSTYLE: style name'),
                PN('MLEADERSTYLE: arrow scale <1>'),
                PN('MLEADERSTYLE: text scale <1>'),
                PTxt('MLEADERSTYLE: color (hex or "keep")'),
            ],
            complete: (_p, values) => {
                const name = String(values[0] ?? 'Standard').trim();
                const id = name.toLowerCase().replace(/\s+/g, '-');
                const st = useLeaderStyleStore.getState();
                const base = st.styles[id] ?? st.styles[st.activeId];
                const color = String(values[3] ?? 'keep').trim();
                st.save({
                    id, name,
                    arrowScale: valueNum(values[1]) || 1,
                    textScale: valueNum(values[2]) || 1,
                    color: color && color !== 'keep' ? color : base.color,
                });
                return `Leader style "${name}" saved and set current.`;
            },
        });
        return 'MLEADERSTYLE: enter name';
    },
    'ann.leaderCollect': () => {
        const list = requireSelection('MLEADERCOLLECT');
        if (!list) return 'MLEADERCOLLECT: select leaders first.';
        const leaders = list.filter((e) => e.type === 'leader');
        if (leaders.length === 0) return 'MLEADERCOLLECT: no leaders selected.';
        // Bubble stack: order leaders by tip height and re-space their
        // landings into an evenly spaced column (visible collection).
        const sorted = [...leaders].sort((a, b) =>
            ((b.data.tip as number[])?.[1] ?? 0) - ((a.data.tip as number[])?.[1] ?? 0));
        const stackX = Math.max(...sorted.map((l) => ((l.data.landing as number[])?.[0] ?? 0)));
        const baseY = (sorted[0].data.landing as number[])?.[1] ?? 0;
        const ids = sorted.map((l) => l.id);
        undoableModify(ids, (d, e) => {
            const idx = ids.indexOf(e.id);
            const tip = (d.tip as number[]) ?? [0, 0, 0];
            const y = baseY - idx * 0.5;
            return {
                ...d,
                landing: [stackX, y, tip[2]],
                textPosition: [stackX + 0.15, y, tip[2]],
                collected: `${idx}`,
            };
        });
        return `MLEADERCOLLECT: ${ids.length} leaders stacked into a bubble column.`;
    },
    'ann.leaderAlign': () => {
        const list = requireSelection('MLEADERALIGN');
        if (!list) return 'MLEADERALIGN: select leaders first.';
        const leaders = list.filter((e) => e.type === 'leader');
        if (leaders.length < 2) return 'MLEADERALIGN: pick 2+ leaders.';
        const ys = leaders.map((e) => ((e.data.landing as number[]) ?? [0, 0, 0])[1]);
        const avg = ys.reduce((a, b) => a + b, 0) / ys.length;
        leaders.forEach((e) => {
            const land = (e.data.landing as number[]) ?? [0, 0, 0];
            undoableModify([e.id], (d) => ({ ...d, landing: [land[0], avg, land[2] ?? 0] }));
        });
        return `${leaders.length} leaders aligned.`;
    },
    'ann.field': () => {
        runInteraction({
            steps: [PK('FIELD: bind to an object'), PT('FIELD: place text at')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const src = entities[picks[0]];
                if (!src) return 'FIELD: invalid pick.';
                const poly = entityToPolyline(src);
                const area = poly && poly.length >= 3 ? polygonArea(poly) : 0;
                const entity = textEntity(`Area = ${area.toFixed(2)}`, [pts[0].x, pts[0].y, pts[0].z], { styleId: 'field' });
                // Persist the source binding so UPDATEFIELD can recompute the
                // live value; the raw {{...}} markup is not rendered.
                entity.data.fieldRef = src.id;
                undoableAdd([entity]);
                return 'Dynamic field text placed (live area binding).';
            },
        });
        return 'FIELD: pick an object';
    },
    'ann.updateField': () => {
        const list = requireSelection('UPDATEFIELD');
        if (!list) return 'UPDATEFIELD: select field texts first.';
        let refreshed = 0;
        list.forEach((e) => {
            const refId = e.data.fieldRef as string | undefined;
            if (!refId) return;
            const { entities } = useAppStore.getState();
            const src = entities[refId];
            if (!src) return;
            const poly = entityToPolyline(src);
            const area = poly && poly.length >= 3 ? polygonArea(poly).toFixed(2) : '—';
            undoableModify([e.id], (d) => ({ ...d, content: `Area = ${area}` }));
            refreshed++;
        });
        return refreshed > 0 ? `UPDATEFIELD: ${refreshed} field(s) refreshed.` : 'UPDATEFIELD: no bound field texts selected.';
    },
};
